import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/common/Breadcrumb";
import ContactForm from "@/components/feature/ContactForm";
import { site, SITE_ORIGIN } from "@/config/site";
import { testimonials } from "@/mocks/site";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const principles = [
  {
    icon: "ri-file-text-line",
    title: "先讲清楚，再下判断",
    desc: "每一篇稿子都会先把事情的来龙去脉交代完整，观点放在事实之后，而不是用一句带情绪的结论当开头。",
  },
  {
    icon: "ri-link",
    title: "尽量标注来源",
    desc: "涉及公开信息的内容会说明出处，遇到无法核实的情节，我们会明确写成「尚未确认」，而不是默认当作事实。",
  },
  {
    icon: "ri-user-heart-line",
    title: "把当事人当人看",
    desc: "讨论作品和现象可以，但我们尽量避免对个人的外貌、家庭和私生活做评价，也不传播未有依据的指控。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `关于${site.name}`,
      description: `${site.name}的品牌介绍、内容原则与联系方式。`,
      url: `${SITE_ORIGIN}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: `联系${site.name}`,
      url: `${SITE_ORIGIN}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.name,
      url: `${SITE_ORIGIN}/`,
      email: site.email,
      description: site.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 诱惑吃瓜的内容原则与联系方式"
        description="了解诱惑吃瓜的品牌定位、内容原则与团队理念，以及投稿、纠错与合作的联系方式。我们坚持先把事实讲清楚，再表达观点。"
        keywords="关于诱惑吃瓜,内容原则,投稿合作,联系我们"
        path="/about"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-6 md:py-12">
        <div className="max-w-4xl">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            关于 {site.name}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一个把娱乐热点整理清楚的轻量内容站。我们不追最快的速度，更在意你点进来之后，能不能真的看懂这件事的前因后果。
          </p>
        </div>
      </div>

      <section aria-labelledby="story-title" className="px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20editorial%20workspace%20with%20a%20wooden%20desk%2C%20notebooks%2C%20a%20cup%20of%20tea%20and%20soft%20afternoon%20light%20through%20linen%20curtains%2C%20editorial%20lifestyle%20photography%2C%20cream%20and%20terracotta%20tones&width=1000&height=800&seq=gu-about-01&orientation=landscape"
              alt={`${site.name} 内容团队编辑场景`}
              title={`${site.name} - 内容整理与编辑`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <p className="mb-2 text-xs font-medium tracking-[0.25em] text-primary-600">WHO WE ARE</p>
            <h2 id="story-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们为什么要做这个站
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-600">
              <p>
                {site.name} 诞生于一个很朴素的想法：热点信息越来越碎，想真正搞明白一件事反而越来越难。与其在各个平台之间来回跳转，不如有人把它整理成一条完整的时间线。
              </p>
              <p>
                所以我们给自己定了一个不算讨巧的目标——把速度让出一点，把清楚留下来。目前站内累计收录了 {articles.length} 篇整理稿，分布在 {categories.length} 个常驻栏目里，
                每一篇都经过二次梳理，而不是简单的搬运。
              </p>
              <p>
                我们不生产传闻，也不替任何人下定论。你在这里看到的，是经过整理的公开信息和作者的观察，两者会被尽量分开摆在你面前。
              </p>
            </div>
            <dl className="mt-7 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
              <div>
                <dt className="text-xs text-foreground-500">常驻栏目</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">{categories.length}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">整理稿件</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">{articles.length}+</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">更新频率</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">每日</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section aria-labelledby="principle-title" className="bg-background-100 px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8 max-w-2xl">
          <p className="mb-2 text-xs font-medium tracking-[0.25em] text-secondary-600">OUR PRINCIPLES</p>
          <h2 id="principle-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            内容原则
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            这三条不是写在墙上的标语，而是每一篇稿子发布前都会过一遍的检查项。
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {principles.map((item, index) => (
            <article
              key={item.title}
              className={`rounded-lg border p-6 ${
                index === 1 ? "border-accent-300 bg-accent-100/60" : "border-background-200 bg-background-50"
              }`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="about-voice-title" className="px-4 py-12 md:px-6 md:py-16">
        <div className="mb-7 text-center">
          <p className="mb-2 text-xs font-medium tracking-[0.25em] text-secondary-600">READERS SAY</p>
          <h2 id="about-voice-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            用户口碑
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {testimonials.map((item) => (
            <figure key={`about-${item.name}`} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <i className="ri-double-quotes-l text-xl text-secondary-400" aria-hidden="true" />
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{item.text}</blockquote>
              <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-500 font-heading text-sm font-bold text-background-50">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section aria-labelledby="contact-title" className="bg-background-100 px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
          <div className="lg:col-span-2">
            <p className="mb-2 text-xs font-medium tracking-[0.25em] text-primary-600">CONTACT US</p>
            <h2 id="contact-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              联系我们
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              有想分享的线索、发现内容有误，或者想谈合作，都可以直接给我们写信。我们尽量在三个工作日内回复，也欢迎留下你的联系方式方便跟进。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-lg" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">投稿与合作邮箱</span>
                  <a href={`mailto:${site.email}`} className="text-foreground-900 transition-colors hover:text-primary-600">
                    {site.email}
                  </a>
                </span>
              </li>
              <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-lg" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">内容更新</span>
                  <span className="text-foreground-900">每日整理，热点随时补充</span>
                </span>
              </li>
              <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-shield-check-line text-lg" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">内容声明</span>
                  <span className="text-foreground-900">本站内容仅供娱乐参考</span>
                </span>
              </li>
            </ul>
            <Link
              to="/news"
              className="mt-6 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
            >
              先去逛逛全部瓜料
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
              <p className="mt-2 text-sm text-foreground-600">
                带 * 的为必填项，内容请尽量写清楚时间、地点和可核实的来源。
              </p>
              <div className="mt-6">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}